import { signOutPending } from "@/lib/sign_out_marker";
import { liveQuery } from "dexie";
import { useEffect, useRef, useState } from "react";
import { readSession } from "@/lib/backend";
import { db } from "@/lib/db";
import { NOTIFICATION_NEED } from "@/lib/notification_need";
import { bridge } from "@/native/impl";
import { planNotifications } from "@/native/notifications";

const EXPLAINED_KEY = "aegae.notificationExplanation.v1";
export function useNativeNotifications(accountId: string | null, trialEndsAt: string | null = null) {
  const identity = useRef({ accountId, generation: 0 });
  if (identity.current.accountId !== accountId) {
    identity.current = { accountId, generation: identity.current.generation + 1 };
  }
  const [explain, setExplain] = useState(false);
  useEffect(() => {
    if (bridge.platform === "web") return;
    const cancelled = bridge.scheduleNotifications([]);
    if (accountId === null) {
      void cancelled.catch((cause: unknown) => { console.warn("Could not cancel reminders:", cause); });
      return;
    }
    let disposed = false;
    const generation = identity.current.generation;
    const current = (): boolean => !disposed && !signOutPending() &&
      (readSession()?.accountId ?? null) === accountId &&
      identity.current.accountId === accountId && identity.current.generation === generation;
    let schedule = cancelled;
    const replan = async (): Promise<void> => {
      const [profile, rules] = await Promise.all([db.profile.get(accountId),
        db.recurringRules.filter((row) => row.userId === accountId).toArray()]);
      if (!current() || !profile) return;
      await bridge.scheduleNotifications(planNotifications({ rules, profile, trialEndsAt, now: new Date() }), current);
    };
    const subscription = liveQuery(async () => {
      await Promise.all([db.recurringRules.filter((row) => row.userId === accountId).toArray(), db.profile.get(accountId)]);
    }).subscribe({ next() { schedule = schedule.then(replan).catch((cause: unknown) => { console.warn("Could not schedule reminders:", cause); }); },
      error(cause: unknown) { console.warn("Could not read reminders:", cause); } });
    const need = (): void => { if (localStorage.getItem(EXPLAINED_KEY) !== "seen") setExplain(true); };
    const accepted = (): void => { schedule = schedule.then(replan).catch((cause: unknown) => { console.warn("Could not schedule reminders:", cause); }); };
    const resumed = (): void => { if (!document.hidden) accepted(); };
    document.addEventListener("visibilitychange", resumed);
    window.addEventListener(NOTIFICATION_NEED, need);
    window.addEventListener("tally:notification-permission", accepted);
    return () => { disposed = true; subscription.unsubscribe(); document.removeEventListener("visibilitychange", resumed); window.removeEventListener(NOTIFICATION_NEED, need); window.removeEventListener("tally:notification-permission", accepted); };
  }, [accountId, trialEndsAt]);
  const dismiss = (): void => { localStorage.setItem(EXPLAINED_KEY, "seen"); setExplain(false); };
  const accept = (): void => {
    dismiss();
    void bridge.requestNotificationPermission().then(() => { window.dispatchEvent(new Event("tally:notification-permission")); })
      .catch((cause: unknown) => { console.warn("Could not request notification permission:", cause); });
  };
  return { explain, dismiss, accept };
}
